export type StrictOmit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;

export const shallowEqualArrays = <T>(
  a: ReadonlyArray<T>,
  b: ReadonlyArray<T>,
) => a.length === b.length && a.every((value, index) => value === b[index]);

export function chunkArray<T>(arr: readonly T[], size: number) {
  const result: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
}

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'] as const;

export function humanReadableBytes(bytes: number) {
  let unitIndex = 0;
  let size = bytes;
  while (size >= 1024 && unitIndex < BYTE_UNITS.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size.toFixed(2)} ${BYTE_UNITS[unitIndex]}`;
}

/**
 * Prefer Effect.sleep when in effect context.
 * Only use this when not in effect context.
 */
export function delayMs(ms: number) {
  return new Promise<void>((resolve) => {
    setTimeout(() => {
      resolve();
    }, ms);
  });
}

/**
 * Prefer Effect's Option/Schema decoding in effect context.
 * Only use this when not in effect context.
 */
export const assertDefined = <T>(
  value: T | null | undefined,
  message?: string,
): T => {
  if (value === null || value === undefined) {
    throw new Error(message ?? 'Value is not defined');
  }
  return value;
};

/**
 * Prefer Effect.timeout when in effect context.
 * Only use this when not in effect context.
 */
export const timeoutPromise = <T>(
  promise: Promise<T>,
  ms: number,
): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_resolve, reject) => {
    timeoutId = setTimeout(() => reject(new Error('Promise timed out')), ms);
  });
  return Promise.race([promise, timeout]).finally(() =>
    clearTimeout(timeoutId),
  );
};

export const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

export function formatTimeSeconds(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
}

export function stringToNumberBlurHash(str: string) {
  let hash = 0;
  if (str.length === 0) return hash;
  for (let i = 0; i < str.length; i++) {
    const char = str.codePointAt(i) ?? 0;
    // oxlint-disable-next-line no-bitwise -- hash algorithm uses bitwise shift intentionally
    hash = (hash << 5) - hash + char;
    // oxlint-disable-next-line no-bitwise, unicorn/prefer-math-trunc -- |=0 coerces to 32-bit signed int with wrap-around semantics; Math.trunc() doesn't wrap
    hash |= 0;
  }
  return hash;
}

export const DEFAULT_AUDIO_TITLE = 'Untitled';

export function excelDateToJSDate(serial: number) {
  const date = new Date((serial - 25_569) * 86_400 * 1000);
  return date;
}

export const capitalizeFirstLetter = (str: string) =>
  str.charAt(0).toUpperCase() + str.slice(1);
